import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import Button from '@components/shared/Button';
import { useAppStore } from '@store/appStore';
import { saveSlideConfig, loadSlideConfig } from '@office/settingsService';
import { injectActivityShape, buildShapeDescriptor, getCurrentSlideIndex } from '@office/slideService';
import type { SlideDrawingConfig } from '@types-local/quiz.types';

export default function SlideDrawing(): React.ReactElement {
  const [prompt, setPrompt] = useState('');
  const [timeLimit, setTimeLimit] = useState(120);
  const [timeLimitEnabled, setTimeLimitEnabled] = useState(true);
  const [allowEraser, setAllowEraser] = useState(true);
  const [starValue, setStarValue] = useState(2);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const isOfficeReady = useAppStore((s) => s.isOfficeReady);

  // Repopulate from the current slide's saved config on return.
  useEffect(() => {
    if (!isOfficeReady) return;
    let cancelled = false;
    (async () => {
      const cfg = loadSlideConfig(await getCurrentSlideIndex());
      if (cancelled || cfg?.type !== 'slide_drawing') return;
      setPrompt(cfg.prompt);
      setTimeLimitEnabled(cfg.timeLimit !== null);
      if (cfg.timeLimit !== null) setTimeLimit(cfg.timeLimit);
      setAllowEraser(cfg.allowEraser);
      setStarValue(cfg.starValue);
    })();
    return () => { cancelled = true; };
  }, [isOfficeReady]);

  const handleSave = async () => {
    if (!prompt.trim()) return;
    setSaving(true);
    try {
      const config: SlideDrawingConfig = {
        type: 'slide_drawing',
        prompt: prompt.trim(),
        backgroundSlideImageUrl: null,
        timeLimit: timeLimitEnabled ? timeLimit : null,
        allowEraser,
        starValue,
      };
      const slideIndex = await getCurrentSlideIndex();
      const shapeName = `IQ_QUIZ_DRAW_${slideIndex.toString().padStart(2, '0')}`;
      await saveSlideConfig(slideIndex, config, shapeName);
      await injectActivityShape(buildShapeDescriptor(config, shapeName), slideIndex);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center text-base">🎨</div>
        <div>
          <h2 className="text-sm font-bold text-slate-900">Slide Drawing</h2>
          <p className="text-[10px] text-slate-500">Students draw on the slide background</p>
        </div>
      </div>

      <div className="rounded-xl bg-purple-500/10 border border-purple-500/20 px-3 py-2.5">
        <p className="text-[11px] text-purple-300 leading-relaxed">
          📌 The current slide's background will be automatically sent to students as their drawing canvas.
        </p>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Drawing Prompt</label>
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
          placeholder="Draw the water cycle and label its stages…"
          rows={3}
          className="w-full bg-surface-elevated border border-surface-border rounded-xl px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 resize-none focus:outline-none focus:border-purple-500 transition-colors" />
      </div>

      <div className="space-y-2.5">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Settings</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-700">⏱ Time Limit</span>
          <div className="flex items-center gap-2">
            {timeLimitEnabled && (
              <input type="number" min={30} max={600} step={10} value={timeLimit} onChange={(e) => setTimeLimit(Number(e.target.value))}
                className="w-14 bg-surface-elevated border border-surface-border rounded-lg px-2 py-1 text-xs text-slate-900 text-center focus:outline-none" />
            )}
            <button onClick={() => setTimeLimitEnabled((v) => !v)}
              className={`w-10 h-5 rounded-full transition-all ${timeLimitEnabled ? 'bg-purple-500' : 'bg-surface-elevated border border-surface-border'}`}>
              <div className={`w-3.5 h-3.5 rounded-full bg-white mx-auto transition-transform ${timeLimitEnabled ? 'translate-x-2.5' : '-translate-x-2.5'}`} />
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-700">⬜ Allow Eraser</span>
          <button onClick={() => setAllowEraser((v) => !v)}
            className={`w-10 h-5 rounded-full transition-all ${allowEraser ? 'bg-purple-500' : 'bg-surface-elevated border border-surface-border'}`}>
            <div className={`w-3.5 h-3.5 rounded-full bg-white mx-auto transition-transform ${allowEraser ? 'translate-x-2.5' : '-translate-x-2.5'}`} />
          </button>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-700">⭐ Stars Award</span>
          <div className="flex items-center gap-1">
            {[1, 2, 3].map((v) => (
              <button key={v} onClick={() => setStarValue(v)}
                className={`w-7 h-7 rounded-lg text-xs font-bold transition-all ${starValue === v ? 'bg-yellow-500 text-white' : 'bg-surface-elevated text-slate-500'}`}>
                {v}⭐
              </button>
            ))}
          </div>
        </div>
      </div>

      <Button fullWidth onClick={handleSave} loading={saving}
        disabled={!prompt.trim() || !isOfficeReady} variant={saved ? 'secondary' : 'primary'}
        icon={saved ? <Check size={14} /> : undefined}
        className={saved ? '' : 'bg-purple-600 hover:bg-purple-700'}>
        {saved ? 'Saved to Slide!' : 'Add to Slide'}
      </Button>
    </div>
  );
}
